import { DEPLOYMENT_ID_CONTENT } from 'xgen-config';
import { renderPaginationControls, setPaginationPageChangeHandler } from 'xgen-ui';

const MAX_ARTICLE_RESULTS = window.itemsPerPage || 9;
const ARTICLE_IMAGE_WIDTHS = [400, 600, 800];
const SEARCH_INPUT_SEL = '.content-search-form__input';
const SEARCH_FORM_SEL = '.content-search-form__form';
const PAGINATION_SEL = '#xgen-pagination';
const RESULTS_SEL = '.content-search-results';
const FILTERS_SEL = '.content-search-filters';
const MIN_QUERY_LEN_FOR_REMOTE = 3;
const URL_PARAM_QUERY = 'q';
const URL_PARAM_FILTER = 'filter_tag';
const FILTER_ALL = 'all-articles';
const allowedCategories = ['Best of 2026', 'Buying Guides', 'Comparisons', 'New Products', 'Parenting 101'];

let currentQuery = '';
let currentPage = 0;
let currentFilter = FILTER_ALL;
let latestRequestId = 0;
let availableTagFacets = {};
let lastFacetQuery = '';

function debounce(func, wait) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
}

function getArticleTitle(article) {
    return article.prod_name || article.title || article.variants?.[0]?.title || '';
}

function isArticleItem(item) {
    return item.type === 'article' || String(item.prod_code || '').startsWith('article');
}

function resolveMediaUrl(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        return value.url || value.src || value.image_url || value.image || '';
    }
    return '';
}

function getArticleUrl(article) {
    const rawUrl = article.product_url || article.url || article.variants?.[0]?.url;
    if (rawUrl) {
        try {
            return new URL(rawUrl, window.location.origin).pathname;
        } catch {
            return rawUrl;
        }
    }
    const blogHandle = article.blog?.handle || 'learning-center';
    return article.handle ? `/blogs/${blogHandle}/${article.handle}` : '';
}

function getArticleImage(article) {
    const candidates = [
        article.image,
        article.thumbnail,
        article.featured_image,
        article.images?.[0],
        article.variants?.[0]?.image,
        article.variants?.[0]?.images?.[0],
    ];
    for (const candidate of candidates) {
        const url = resolveMediaUrl(candidate);
        if (url) return url;
    }
    return '';
}

function isCompleteArticle(article) {
    return Boolean(getArticleTitle(article) && getArticleUrl(article) && getArticleImage(article));
}

function pickCompleteArticles(items, max = MAX_ARTICLE_RESULTS) {
    return (items || [])
        .filter((item) => isArticleItem(item) && isCompleteArticle(item))
        .slice(0, max);
}

function normalizeArticleImageBase(imageSrc) {
    if (!imageSrc) return '';
    return String(imageSrc).split('&width=')[0].split('?width=')[0];
}

function isShopifyArticleCdnUrl(url) {
    return /\/articles\//.test(url);
}

function buildArticleImageSources(imageSrc) {
    const base = normalizeArticleImageBase(imageSrc);
    if (!base) return { src: '', srcset: '' };

    if (isShopifyArticleCdnUrl(base)) {
        return { src: base, srcset: '' };
    }

    const joiner = base.includes('?') ? '&' : '?';
    const src = `${base}${joiner}width=800`;
    const srcset = ARTICLE_IMAGE_WIDTHS.map((width) => `${base}${joiner}width=${width} ${width}w`).join(', ');
    return { src, srcset };
}

function escapeHtmlAttr(s) {
    return String(s).replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function normalizeFilterTag(value) {
    const tag = String(value || '').trim();
    if (!tag || tag === FILTER_ALL) return FILTER_ALL;
    return allowedCategories.includes(tag) ? tag : FILTER_ALL;
}

function buildContentSearchContext(filter = currentFilter) {
    const tag = normalizeFilterTag(filter);
    if (tag === FILTER_ALL) return {};
    return { tags: [tag] };
}

function addAllowedTagCount(counts, tag, rawCount = 1) {
    const name = String(tag || '').trim();
    if (!allowedCategories.includes(name)) return;
    const numericCount = typeof rawCount === 'object' && rawCount
        ? parseInt(rawCount.count ?? rawCount.doc_count ?? 1, 10)
        : parseInt(rawCount, 10);
    const count = Number.isNaN(numericCount) ? 1 : numericCount;
    if (count <= 0) return;
    counts[name] = (counts[name] || 0) + count;
}

function getTagCountsFromItems(items) {
    const counts = {};
    for (const item of items || []) {
        for (const tag of item.tags || []) {
            addAllowedTagCount(counts, tag);
        }
    }
    return counts;
}

function getTagFacetCounts(result) {
    const counts = {};
    const facetsPayload = result?.facets;
    const groups = Array.isArray(facetsPayload)
        ? facetsPayload
        : facetsPayload
            ? [facetsPayload]
            : [];

    for (const group of groups) {
        const facetMap = group?.facets && typeof group.facets === 'object' ? group.facets : group;
        if (!facetMap || typeof facetMap !== 'object') continue;

        const tagFacets = facetMap.tags || facetMap.tag || facetMap.Tags;
        if (!tagFacets) continue;

        if (Array.isArray(tagFacets)) {
            for (const entry of tagFacets) {
                if (typeof entry === 'string') {
                    addAllowedTagCount(counts, entry);
                } else if (entry && typeof entry === 'object') {
                    addAllowedTagCount(
                        counts,
                        entry.value || entry.name || entry.tag || entry.label,
                        entry.count ?? entry.doc_count ?? 1
                    );
                }
            }
            continue;
        }

        if (typeof tagFacets === 'object') {
            Object.entries(tagFacets).forEach(([tag, rawCount]) => addAllowedTagCount(counts, tag, rawCount));
        }
    }

    return counts;
}

function rememberTagFacets(query, filter, counts) {
    if (query !== lastFacetQuery) {
        availableTagFacets = {};
        lastFacetQuery = query;
    }

    if (filter === FILTER_ALL && Object.keys(counts).length) {
        availableTagFacets = counts;
    }
}

function getContentSearchParamsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const query = (params.get(URL_PARAM_QUERY) || '').trim();
    const filter = normalizeFilterTag(params.get(URL_PARAM_FILTER));
    return { query, filter };
}

function updateContentSearchUrl({ query = currentQuery, filter = currentFilter } = {}) {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const trimmedQuery = String(query || '').trim();
    const normalizedFilter = normalizeFilterTag(filter);

    if (trimmedQuery) {
        params.set(URL_PARAM_QUERY, trimmedQuery);
    } else {
        params.delete(URL_PARAM_QUERY);
    }
    // Uncommeting this when we want to add filter_tag to the URL
    // if (normalizedFilter !== FILTER_ALL) {
    //     params.set(URL_PARAM_FILTER, normalizedFilter);
    // } else {
    //     params.delete(URL_PARAM_FILTER);
    // }

    history.replaceState({}, '', url.toString());
}

function setSearchInputValue(query) {
    const input = document.querySelector(SEARCH_INPUT_SEL);
    if (input) input.value = query || '';
}

function renderArticleBadge(article) {
    const categories = article.tags || [];
    const uniqueCategories = [...new Set(categories)].filter((category) => allowedCategories.includes(category)).sort();
    // If there are multiple categories, return the first 2 categories, both on left side one below the other and if there is only one category, return the category on the left side
    const firstCategory = uniqueCategories[0] || '';
    const secondCategory = uniqueCategories[1] || '';
    if (firstCategory && secondCategory) {
        return `<div class="article-badge-container"><span class="article-badge">${firstCategory}</span><span class="article-badge">${secondCategory}</span></div>`;
    }
    return `${firstCategory.length > 0 ? `<div class="article-badge-container"><span class="article-badge">${firstCategory}</span></div>` : ''}`;
}

function buildArticleHTML(article) {
    const title = getArticleTitle(article);
    const url = getArticleUrl(article);
    const imageSrc = getArticleImage(article);
    const badge = renderArticleBadge(article);

    if (!title || !url || !imageSrc) return '';

    const escapedTitle = escapeHtmlAttr(title);
    const { src, srcset } = buildArticleImageSources(imageSrc);
    const category = article.tags?.[0]?.toLowerCase()?.replaceAll(' ', '-') || '';

    return `
      <li class="content-search-results__item" data-category="${category}">
          <div class="article-summary__media">
            <a href="${url}" class="article-summary__media-link" aria-label="Go to blog post for ${escapedTitle}">
              <img
                src="${src}"
                alt="${escapedTitle}"
                ${srcset ? `srcset="${srcset}"` : ''}
                width="800"
                height="300"
                loading="lazy"
                sizes="(min-width: 1024px) calc(calc(92vw / 3) * 0.36), calc(91vw * 0.36)"
                class="article-summary__image fit-image"
              >
              </a>
              ${badge}
          </div>
          <div class="article-summary__info">
            <div class="article-summary__info-content">
              <h3 class="article-summary__title heading heading--6">
                <a href="${url}" class="article-summary__title-link">${title}</a>
              </h3>
              <p class="article-summary__excerpt">${article.short_description || ''}</p>
            </div>
            <div class="article-summary__info-read-more">
              <a href="${url}" class="text-button" aria-label="Read more of ${escapedTitle}">Read more &rarr;</a>
            </div>
          </div>
      </li>`;
}

function setContentSearchLoading(isLoading) {
    const results = document.querySelector(RESULTS_SEL);
    const filters = document.querySelector(FILTERS_SEL);
    const loader = results?.querySelector('.content-search-results__loader');

    results?.classList.toggle('is-loading', isLoading);
    results?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    filters?.classList.toggle('is-loading', isLoading);

    if (loader) loader.hidden = !isLoading;
}

function getPaginationContainer() {
    return document.querySelector(PAGINATION_SEL);
}

function clearPagination() {
    const container = getPaginationContainer();
    if (container) container.innerHTML = '';
}

function renderEmptyState(query = '', isError = false) {
    const grid = document.querySelector('.content-search-results__grid');
    const header = document.querySelector('.content-search-results__header');
    if (header) header.innerHTML = '';
    if (!grid) return;

    clearPagination();

    const title = isError
        ? 'Search is currently unavailable'
        : `No articles match "${escapeHtmlAttr(query)}"`;

    const subtitle = isError
        ? 'Please try again in a moment, or browse our popular topics below.'
        : 'Try a broader term, or browse by topic instead — new guides are added every week.';

    grid.innerHTML = `
        <div class="content-search-empty">
            <div class="content-search-empty__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
            </div>
            <h3 class="content-search-empty__title">${title}</h3>
            <p class="content-search-empty__subtitle">${subtitle}</p>
            <div class="content-search-empty__actions">
                <button type="button" class="content-search-empty__pill" data-filter="Best of 2026">Best of 2026</button>
                <button type="button" class="content-search-empty__pill" data-filter="Buying Guides">Buying Guides</button>
                <button type="button" class="content-search-empty__pill" data-filter="Comparisons">Comparisons</button>
            </div>
        </div>
    `;
}

function clearSearchResults() {
    const grid = document.querySelector('.content-search-results__grid');
    const header = document.querySelector('.content-search-results__header');
    const filtersContainer = document.querySelector('.content-search-filters__list');

    if (grid) grid.innerHTML = '';
    if (header) header.innerHTML = '';
    if (filtersContainer) filtersContainer.innerHTML = '';
    clearPagination();
    currentQuery = '';
    currentPage = 0;
    currentFilter = FILTER_ALL;
    availableTagFacets = {};
    lastFacetQuery = '';
    setContentSearchLoading(false);
    updateContentSearchUrl();
}

function setActiveFilterButton(filterValue = currentFilter) {
    const filtersContainer = document.querySelector('.content-search-filters__list');
    if (!filtersContainer) return;

    filtersContainer.querySelectorAll('.content-search-filters__button').forEach((btn) => {
        const isActive = btn.getAttribute('data-filter') === filterValue;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        btn.closest('.content-search-filters__item')?.classList.toggle('active', isActive);
    });
}

function renderContentSearchContent(items, query) {
    const grid = document.querySelector('.content-search-results__grid');
    if (!grid) return;

    if (!items || !items.length) {
        renderEmptyState(query, false);
        return;
    }
    const allCardsHTML = items.map(buildArticleHTML).join('');
    grid.innerHTML = allCardsHTML;
}

function renderContentSearchError() {
    const query = document.querySelector(SEARCH_INPUT_SEL)?.value.trim() || '';
    renderEmptyState(query, true);
}

function renderContentSearchHeader(totalResults) {
    const header = document.querySelector('.content-search-results__header');
    if (!header) return;

    const query = document.querySelector(SEARCH_INPUT_SEL)?.value.trim();
    if (!query || !totalResults) {
        header.textContent = '';
        return;
    }

    const heading = currentFilter !== FILTER_ALL
        ? escapeHtmlAttr(currentFilter)
        : `Results for "${escapeHtmlAttr(query)}"`;

    header.innerHTML = `
        <h3>${heading}</h3>
        <p><strong>${totalResults}</strong> articles found</p>
    `;
}

function buildArticleFilters(facetCounts = {}, items = []) {
    const filters = [];
    const uniqueCategories = [...new Set([
        ...Object.keys(availableTagFacets),
        ...Object.keys(facetCounts),
        ...Object.keys(getTagCountsFromItems(items)),
        ...(currentFilter !== FILTER_ALL ? [currentFilter] : []),
    ])]
        .filter((category) => allowedCategories.includes(category))
        .sort();

    for (const category of uniqueCategories) {
        const isActive = currentFilter === category;
        filters.push(`<li class="content-search-filters__item${isActive ? ' active' : ''}">
            <button type="button" class="content-search-filters__button${isActive ? ' active' : ''}" data-filter="${escapeHtmlAttr(category)}" aria-pressed="${isActive ? 'true' : 'false'}">
                <span>${escapeHtmlAttr(category)}</span>
                <!-- <span class="content-search-filters__button-count">(${facetCounts[category] || 0})</span> -->
            </button>
        </li>`);
    }

    const allActive = currentFilter === FILTER_ALL;
    const allArticlesFilter = `<li class="content-search-filters__item${allActive ? ' active' : ''}">
              <button type="button" class="content-search-filters__button${allActive ? ' active' : ''}" data-filter="${FILTER_ALL}" aria-pressed="${allActive ? 'true' : 'false'}">
                <span>All Articles</span>
              </button>
            </li>`;

    filters.unshift(allArticlesFilter);

    const filtersContainer = document.querySelector('.content-search-filters__list');
    if (filtersContainer) {
        filtersContainer.innerHTML = filters.join('');
    }
}

function renderContentSearchResults(result, query, pageIndex) {
    const items = result?.items || [];
    const totalResults = result?.totalResults || 0;
    const totalPages = Math.max(1, Math.ceil(totalResults / MAX_ARTICLE_RESULTS));
    const tagFacets = getTagFacetCounts(result);
    const rememberedCounts = currentFilter === FILTER_ALL
        ? (Object.keys(tagFacets).length ? tagFacets : getTagCountsFromItems(items))
        : tagFacets;

    rememberTagFacets(query, currentFilter, rememberedCounts);

    if (totalResults > 0) {
        renderContentSearchHeader(totalResults);
    } else {
        const header = document.querySelector('.content-search-results__header');
        if (header) header.innerHTML = '';
    }

    renderContentSearchContent(items, query);
    buildArticleFilters(tagFacets, items);
    setActiveFilterButton(currentFilter);

    if (totalPages <= 1 || items.length === 0) {
        clearPagination();
        return;
    }

    requestAnimationFrame(() => {
        renderPaginationControls(totalPages, pageIndex);
    });
}

async function fetchContentSearch(query, page = 0) {
    const requestId = ++latestRequestId;
    currentQuery = query;
    currentPage = page;
    updateContentSearchUrl();

    const searchContext = buildContentSearchContext();
    setContentSearchLoading(true);

    try {
        const contentResult = await window.xgenClient.search.getResults({
            query,
            queryId: crypto.randomUUID(),
            options: {
                collection: 'default',
                deploymentId: DEPLOYMENT_ID_CONTENT,
                pageSize: MAX_ARTICLE_RESULTS,
                page,
                context: searchContext,
                facets: true,
            },
            page,
            context: searchContext,
        });

        if (requestId !== latestRequestId) return;
        renderContentSearchResults(contentResult, query, page);
    } catch (err) {
        if (requestId !== latestRequestId) return;
        console.error('Content search fetch error:', err);
        renderContentSearchError();
    } finally {
        if (requestId === latestRequestId) {
            setContentSearchLoading(false);
        }
    }
}

function applyFilterChange(filterValue) {
    const nextFilter = normalizeFilterTag(filterValue);
    if (nextFilter === currentFilter) return;

    currentFilter = nextFilter;
    setActiveFilterButton(currentFilter);
    updateContentSearchUrl();

    if (currentQuery.length >= MIN_QUERY_LEN_FOR_REMOTE) {
        fetchContentSearch(currentQuery, 0);
    }
}

function hydrateContentSearchFromUrl() {
    const { query, filter } = getContentSearchParamsFromUrl();
    currentFilter = filter;
    setSearchInputValue(query);

    if (query.length >= MIN_QUERY_LEN_FOR_REMOTE) {
        fetchContentSearch(query, 0);
        return;
    }

    updateContentSearchUrl({ query: '', filter: FILTER_ALL });
}

function handleEmptyStatePillClick(pill) {
    const tag = (pill.getAttribute('data-filter') || pill.textContent || '').trim();
    if (!tag) return;

    setSearchInputValue(tag);
    document.querySelector(SEARCH_INPUT_SEL)?.focus();
    currentFilter = normalizeFilterTag(tag);
    fetchContentSearch(tag, 0);
}

function handlePopState() {
    const { query, filter } = getContentSearchParamsFromUrl();
    currentFilter = filter;
    setSearchInputValue(query);

    if (query.length >= MIN_QUERY_LEN_FOR_REMOTE) {
        fetchContentSearch(query, 0);
        return;
    }

    clearSearchResults();
}

let searchInitialized = false;

const initContentSearch = () => {
    if (searchInitialized || !window.xgenClient) return;
    searchInitialized = true;

    setPaginationPageChangeHandler((pageIndex) => {
        if (!currentQuery) return;
        document.querySelector('.content-search__header')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return fetchContentSearch(currentQuery, pageIndex);
    });

    document.addEventListener('submit', (e) => {
        if (e.target?.matches?.(SEARCH_FORM_SEL)) {
            e.preventDefault();
            e.stopPropagation();
        }
    }, true);

    const handleContentSearch = debounce((query) => {
        currentFilter = FILTER_ALL;
        fetchContentSearch(query, 0);
    }, 500);

    document.addEventListener('input', (e) => {
        const input = e.target;
        if (!input?.matches?.(SEARCH_INPUT_SEL)) return;

        const query = input.value.trim();
        if (query.length >= MIN_QUERY_LEN_FOR_REMOTE) {
            handleContentSearch(query);
        } else {
            clearSearchResults();
        }
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target?.matches?.(SEARCH_INPUT_SEL)) {
            e.preventDefault();
        }
    });

    document.addEventListener('click', (e) => {
        const pill = e.target.closest('.content-search-empty__pill');
        if (!pill) return;
        handleEmptyStatePillClick(pill);
    });

    window.addEventListener('popstate', handlePopState);

    hydrateContentSearchFromUrl();
    document.dispatchEvent(new CustomEvent('content-search-initialized'));
};

let filtersInitialized = false;

document.addEventListener('content-search-initialized', () => {
    if (filtersInitialized) return;
    filtersInitialized = true;

    document.body.addEventListener('click', (e) => {
        const button = e.target.closest('.content-search-filters__button');
        if (!button) return;
        applyFilterChange(button.getAttribute('data-filter') || FILTER_ALL);
    });
});

if (window.xgenClient) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initContentSearch);
    } else {
        initContentSearch();
    }
} else {
    document.addEventListener('xgen-ready', initContentSearch, { once: true });
}
